import { Input } from "@/components/shadcn/input";
import { DatePickerInput } from "@/components/Form/DatePickerField";
import { Label } from "@/components/shadcn/label";
import { Switch } from "@/components/shadcn/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import type { FieldValidation } from "@/generated";

const TEXTUAL_TYPES = new Set(["text", "textarea", "email", "phone", "url"]);
const FORMAT_TYPES = new Set(["email", "phone", "url"]);

// Normalization (trim, letter case) applies to the same textual types but is
// edited apart from validation: it rewrites values, it never flags them.
export const FORMATTING_TYPES = TEXTUAL_TYPES;

export const VALIDATABLE_TYPES = new Set([
  "text",
  "textarea",
  "number",
  "date",
  "email",
  "phone",
  "url",
  "select",
  "lookup",
  "table",
  "file",
]);

const NAMED_FORMAT_OPTIONS = [
  { value: "", label: "None" },
  { value: "iban", label: "IBAN" },
  { value: "luhn", label: "Card number (Luhn)" },
  { value: "ean", label: "EAN / GTIN barcode" },
  { value: "nif_pt", label: "Portuguese NIF" },
];

// Cross-case uniqueness applies to identifier-ish values only (matches the
// backend's _UNIQUE_TYPES).
const UNIQUE_TYPES = new Set([
  "text",
  "textarea",
  "email",
  "phone",
  "url",
  "select",
]);

const numberOrNull = (raw: string): number | null =>
  raw.trim() === "" || Number.isNaN(Number(raw)) ? null : Number(raw);

const SmallField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex-1 min-w-0 space-y-2">
    <Label>{label}</Label>
    {children}
  </div>
);

const SwitchRow = ({
  id,
  label,
  description,
  checked,
  onChange,
  isDisabled,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  isDisabled?: boolean;
}) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <Label htmlFor={id}>{label}</Label>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
    <Switch
      id={id}
      checked={checked}
      onCheckedChange={onChange}
      disabled={isDisabled}
    />
  </div>
);

/**
 * Per-type shape-validation rule inputs. Shared between the property dialog
 * and the table-column popover; `type` decides which rules render.
 */
export const ValidationConfigEditor = ({
  type,
  value,
  onChange,
  idPrefix,
  isDisabled,
  context = "field",
}: {
  type: string;
  value: FieldValidation | null;
  onChange: (value: FieldValidation) => void;
  idPrefix: string;
  isDisabled?: boolean;
  context?: "field" | "column";
}) => {
  const rules: FieldValidation = value ?? {};
  const patch = (p: Partial<FieldValidation>) => onChange({ ...rules, ...p });
  // "lookup" is picker-level sugar for select + source.
  const normalizedType = type === "lookup" ? "select" : type;
  const issues = validationConfigIssues(rules);

  return (
    <div className="flex flex-col gap-4">
      {issues.length > 0 && (
        <div className="flex flex-col gap-1">
          {issues.map((issue, i) => (
            <p key={i} className="text-xs text-destructive">
              {issue}
            </p>
          ))}
        </div>
      )}
      {TEXTUAL_TYPES.has(normalizedType) && (
        <>
          <div className="flex gap-2">
            <SmallField label="Min length">
              <Input
                type="number"
                min={0}
                value={rules.min_length ?? ""}
                placeholder="e.g. 3"
                onChange={(e) =>
                  patch({ min_length: numberOrNull(e.target.value) })
                }
                disabled={isDisabled}
                className="h-8"
              />
            </SmallField>
            <SmallField label="Max length">
              <Input
                type="number"
                min={1}
                value={rules.max_length ?? ""}
                placeholder="e.g. 50"
                onChange={(e) =>
                  patch({ max_length: numberOrNull(e.target.value) })
                }
                disabled={isDisabled}
                className="h-8"
              />
            </SmallField>
          </div>
          <SmallField label="Pattern (regular expression, full match)">
            <Input
              value={rules.pattern ?? ""}
              onChange={(e) =>
                patch({ pattern: e.target.value.trim() || null })
              }
              placeholder="e.g. ORD-\d{5}"
              disabled={isDisabled}
              className="h-8 font-mono"
            />
          </SmallField>
          {rules.pattern && (
            <SmallField label="Message shown when the pattern doesn't match">
              <Input
                value={rules.pattern_message ?? ""}
                onChange={(e) =>
                  patch({ pattern_message: e.target.value || null })
                }
                placeholder="e.g. Use the format ORD-#####"
                disabled={isDisabled}
                className="h-8"
              />
            </SmallField>
          )}
          <SmallField label="Known format (checksum-validated)">
            <Select
              value={rules.format ?? "none"}
              onValueChange={(v) =>
                patch({
                  format: (v === "none"
                    ? null
                    : v) as FieldValidation["format"],
                })
              }
              disabled={isDisabled}
            >
              <SelectTrigger className="h-8 w-full">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                {NAMED_FORMAT_OPTIONS.map((o) => (
                  <SelectItem key={o.value || "none"} value={o.value || "none"}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SmallField>
        </>
      )}
      {FORMAT_TYPES.has(normalizedType) && (
        <SwitchRow
          id={`${idPrefix}_check_format`}
          label={`Require a valid ${normalizedType}`}
          description={`Values that are not a valid ${normalizedType} are flagged.`}
          checked={rules.check_format ?? false}
          onChange={(checked) => patch({ check_format: checked })}
          isDisabled={isDisabled}
        />
      )}
      {normalizedType === "number" && (
        <>
          <div className="flex gap-2">
            <SmallField label="Minimum">
              <Input
                type="number"
                value={rules.min_value ?? ""}
                placeholder="e.g. 0"
                onChange={(e) =>
                  patch({ min_value: numberOrNull(e.target.value) })
                }
                disabled={isDisabled}
                className="h-8"
              />
            </SmallField>
            <SmallField label="Maximum">
              <Input
                type="number"
                value={rules.max_value ?? ""}
                placeholder="e.g. 1000"
                onChange={(e) =>
                  patch({ max_value: numberOrNull(e.target.value) })
                }
                disabled={isDisabled}
                className="h-8"
              />
            </SmallField>
          </div>
          <SwitchRow
            id={`${idPrefix}_integer_only`}
            label="Whole numbers only"
            description="Values with a decimal part are flagged."
            checked={rules.integer_only ?? false}
            onChange={(checked) => patch({ integer_only: checked })}
            isDisabled={isDisabled}
          />
          <SmallField label="Max decimal places">
            <Input
              type="number"
              min={0}
              value={rules.max_decimals ?? ""}
              placeholder="e.g. 2"
              onChange={(e) =>
                patch({ max_decimals: numberOrNull(e.target.value) })
              }
              disabled={isDisabled}
              className="h-8"
            />
          </SmallField>
        </>
      )}
      {normalizedType === "date" && (
        <>
          <div className="flex gap-2">
            <SmallField label="Earliest">
              <DatePickerInput
                valueFormat="date"
                value={rules.min_date ?? ""}
                onChange={(value) => patch({ min_date: value || null })}
                onClear={() => patch({ min_date: null })}
                isDisabled={isDisabled}
              />
            </SmallField>
            <SmallField label="Latest">
              <DatePickerInput
                valueFormat="date"
                value={rules.max_date ?? ""}
                onChange={(value) => patch({ max_date: value || null })}
                onClear={() => patch({ max_date: null })}
                isDisabled={isDisabled}
              />
            </SmallField>
          </div>
          <div className="flex gap-2">
            <SmallField label="Earliest (days from today)">
              <Input
                type="number"
                placeholder="e.g. 0 = today"
                value={rules.min_date_offset_days ?? ""}
                onChange={(e) =>
                  patch({ min_date_offset_days: numberOrNull(e.target.value) })
                }
                disabled={isDisabled}
                className="h-8"
              />
            </SmallField>
            <SmallField label="Latest (days from today)">
              <Input
                type="number"
                placeholder="e.g. 30"
                value={rules.max_date_offset_days ?? ""}
                onChange={(e) =>
                  patch({ max_date_offset_days: numberOrNull(e.target.value) })
                }
                disabled={isDisabled}
                className="h-8"
              />
            </SmallField>
          </div>
        </>
      )}
      {context === "field" && normalizedType === "table" && (
        <div className="flex gap-2">
          <SmallField label="Min rows">
            <Input
              type="number"
              min={0}
              value={rules.min_rows ?? ""}
              placeholder="e.g. 1"
              onChange={(e) =>
                patch({ min_rows: numberOrNull(e.target.value) })
              }
              disabled={isDisabled}
              className="h-8"
            />
          </SmallField>
          <SmallField label="Max rows">
            <Input
              type="number"
              min={1}
              value={rules.max_rows ?? ""}
              placeholder="e.g. 10"
              onChange={(e) =>
                patch({ max_rows: numberOrNull(e.target.value) })
              }
              disabled={isDisabled}
              className="h-8"
            />
          </SmallField>
        </div>
      )}
      {context === "field" && UNIQUE_TYPES.has(normalizedType) && (
        <>
          <SwitchRow
            id={`${idPrefix}_unique`}
            label="Unique"
            description="The same value can't be used by two cases."
            checked={rules.unique ?? false}
            onChange={(checked) => patch({ unique: checked })}
            isDisabled={isDisabled}
          />
          {rules.unique && (
            <SwitchRow
              id={`${idPrefix}_unique_ci`}
              label="Ignore letter case"
              description="Values that differ only in casing count as the same."
              checked={rules.unique_case_insensitive ?? false}
              onChange={(checked) =>
                patch({ unique_case_insensitive: checked })
              }
              isDisabled={isDisabled}
            />
          )}
          {rules.unique && (
            <SmallField label="Compared against">
              <Select
                value={rules.unique_scope ?? "all"}
                onValueChange={(v) =>
                  patch({ unique_scope: v as FieldValidation["unique_scope"] })
                }
                disabled={isDisabled}
              >
                <SelectTrigger className="h-8 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cases</SelectItem>
                  <SelectItem value="open">Open cases only</SelectItem>
                </SelectContent>
              </Select>
            </SmallField>
          )}
        </>
      )}
      {normalizedType === "file" && (
        <>
          <SmallField label="Allowed extensions (comma-separated)">
            <Input
              value={(rules.allowed_extensions ?? []).join(", ")}
              onChange={(e) =>
                patch({
                  allowed_extensions: e.target.value
                    ? e.target.value.split(",").map((s) => s.trim())
                    : null,
                })
              }
              placeholder="e.g. pdf, png, docx"
              disabled={isDisabled}
              className="h-8"
            />
          </SmallField>
          <SmallField label="Max size (MB)">
            <Input
              type="number"
              min={0}
              value={rules.max_file_size_mb ?? ""}
              placeholder="e.g. 25"
              onChange={(e) =>
                patch({ max_file_size_mb: numberOrNull(e.target.value) })
              }
              disabled={isDisabled}
              className="h-8"
            />
          </SmallField>
        </>
      )}
      {context === "column" && normalizedType !== "boolean" && (
        <SwitchRow
          id={`${idPrefix}_unique_in_table`}
          label="Unique within this table"
          description="The same value can't appear in two rows."
          checked={rules.unique_in_table ?? false}
          onChange={(checked) => patch({ unique_in_table: checked })}
          isDisabled={isDisabled}
        />
      )}
      <SwitchRow
        id={`${idPrefix}_strict`}
        label="Strict"
        description="Invalid values are rejected instead of saved with a warning."
        checked={rules.strict ?? false}
        onChange={(checked) => patch({ strict: checked })}
        isDisabled={isDisabled}
      />
    </div>
  );
};

/**
 * Normalization controls (trim, letter case). Rendered as its own dialog
 * section and below the rules in the column popover; writes to the same
 * FieldValidation object as ValidationConfigEditor.
 */
export const FormattingConfigEditor = ({
  value,
  onChange,
  idPrefix,
  isDisabled,
}: {
  value: FieldValidation | null;
  onChange: (value: FieldValidation) => void;
  idPrefix: string;
  isDisabled?: boolean;
}) => {
  const rules: FieldValidation = value ?? {};
  const patch = (p: Partial<FieldValidation>) => onChange({ ...rules, ...p });

  return (
    <div className="flex flex-col gap-4">
      <SwitchRow
        id={`${idPrefix}_trim`}
        label="Trim whitespace"
        description="Leading and trailing spaces are removed before saving."
        checked={rules.trim ?? false}
        onChange={(checked) => patch({ trim: checked })}
        isDisabled={isDisabled}
      />
      <SmallField label="Letter case">
        <Select
          value={rules.text_case ?? "none"}
          onValueChange={(v) =>
            patch({
              text_case: (v === "none"
                ? null
                : v) as FieldValidation["text_case"],
            })
          }
          disabled={isDisabled}
        >
          <SelectTrigger className="h-8 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Keep as typed</SelectItem>
            <SelectItem value="upper">UPPERCASE</SelectItem>
            <SelectItem value="lower">lowercase</SelectItem>
          </SelectContent>
        </Select>
      </SmallField>
    </div>
  );
};

/** Incoherent rule combinations the server would reject; shown inline and
 * as an error dot on the dialog's Validation section. */
export const validationConfigIssues = (
  value: FieldValidation | null | undefined
): string[] => {
  const v = value ?? {};
  const issues: string[] = [];
  if (
    v.min_length != null &&
    v.max_length != null &&
    v.min_length > v.max_length
  )
    issues.push("Min length is greater than max length.");
  if (v.min_value != null && v.max_value != null && v.min_value > v.max_value)
    issues.push("Minimum is greater than maximum.");
  if (v.min_rows != null && v.max_rows != null && v.min_rows > v.max_rows)
    issues.push("Min rows is greater than max rows.");
  if (v.min_date && v.max_date && v.min_date > v.max_date)
    issues.push("Earliest date is after the latest date.");
  if (v.pattern) {
    try {
      new RegExp(v.pattern);
    } catch {
      issues.push("The pattern is not a valid regular expression.");
    }
  }
  return issues;
};

export const hasValidationRules = (
  value: FieldValidation | null | undefined
): boolean =>
  Boolean(
    value &&
    (value.pattern ||
      value.min_length != null ||
      value.max_length != null ||
      value.min_value != null ||
      value.max_value != null ||
      value.integer_only ||
      value.check_format ||
      value.min_date ||
      value.max_date)
  );

const FORMAT_LABELS: Record<string, string> = {
  iban: "IBAN",
  luhn: "card number",
  ean: "EAN / GTIN barcode",
  nif_pt: "Portuguese NIF",
};

/** Human-readable lines describing a field's active rules, for tooltips. */
export const validationSummary = (
  type: string,
  value: FieldValidation | null | undefined
): string[] => {
  const v = value ?? {};
  const out: string[] = [];
  if (v.min_length != null) out.push(`At least ${v.min_length} characters`);
  if (v.max_length != null) out.push(`At most ${v.max_length} characters`);
  if (v.pattern) out.push(`Must match ${v.pattern}`);
  if (v.format) out.push(`Valid ${FORMAT_LABELS[v.format] ?? v.format}`);
  if (v.check_format) out.push(`Must be a valid ${type}`);
  if (v.min_value != null) out.push(`Minimum ${v.min_value}`);
  if (v.max_value != null) out.push(`Maximum ${v.max_value}`);
  if (v.integer_only) out.push("Whole numbers only");
  if (v.max_decimals != null) out.push(`At most ${v.max_decimals} decimals`);
  if (v.min_date) out.push(`On or after ${v.min_date}`);
  if (v.max_date) out.push(`On or before ${v.max_date}`);
  if (v.min_date_offset_days != null)
    out.push(`At least ${v.min_date_offset_days} days from today`);
  if (v.max_date_offset_days != null)
    out.push(`At most ${v.max_date_offset_days} days from today`);
  if (v.min_rows != null) out.push(`At least ${v.min_rows} rows`);
  if (v.max_rows != null) out.push(`At most ${v.max_rows} rows`);
  if (v.unique)
    out.push(
      v.unique_scope === "open"
        ? "Unique across open cases"
        : "Unique across cases"
    );
  if (v.unique_in_table) out.push("Unique within the table");
  if (v.allowed_extensions?.length)
    out.push(`Allowed files: ${v.allowed_extensions.join(", ")}`);
  if (v.max_file_size_mb != null) out.push(`Max ${v.max_file_size_mb} MB`);
  if (v.trim) out.push("Trims whitespace");
  if (v.text_case === "upper") out.push("Uppercased");
  if (v.text_case === "lower") out.push("Lowercased");
  if (v.strict && out.length > 0)
    out.push("Strict: invalid values are rejected");
  return out;
};
